import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, TransitionGroup as _TransitionGroup } from "vue"

const _hoisted_1 = { class: "absolute right-0 top-0 translate-x-1/2 -translate-y-1/2 z-0" }
const _hoisted_2 = ["onClick"]

import { computed, ref } from "vue";
import { Container, Draggable, DropResult } from "vue-dndrop";
import { useOnClickOutside } from "@/composables";
import { useHelpStore, useKeyboardStore } from "@/stores";
import ListSwitcher from "@/components/utils/ListSwitcher.vue";
import LedRuleEdit from "@/components/leds/LedRuleEdit.vue";
import LedRuleView from "@/components/leds/LedRuleView.vue";
import { LedRule } from "@/keyboard";
import { Icon } from "@iconify/vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'LedsArea',
  setup(__props) {

const kb = useKeyboardStore();
const help = useHelpStore();

const numberList = (length: number) =>
  [...Array(length).keys()].map((l) => l.toString());

const selectedRule = ref<number | null>(null);
const root = ref<HTMLElement | null>(null);

useOnClickOutside(root, () => (selectedRule.value = null));

const ledRules = computed(
  () => help.overrideLedRules ?? kb.config.leds[kb.ledConfig]
);

// run actions only when there are no help overrides
function ifNoHelp<A extends unknown[], R>(fn: (...args: A) => R | undefined) {
  return (...params: A) => {
    if (help.overrideLedRules) return;
    return fn(...params);
  };
}

const isExpanded = (id: number) => {
  if (help.selectedLedRule != null) {
    return help.expandLedRule && id == help.selectedLedRule;
  } else {
    return id == selectedRule.value;
  }
};

const drop = ifNoHelp((result: DropResult) => {
  const from = result.removedIndex;
  const to = result.addedIndex;
  if (from == null || to == null) return;

  const config = kb.config.leds[kb.ledConfig];
  if (config == undefined) return; // missing config
  if (from < 0 || from > config.length - 1) return; // out of range
  if (to < 0 || to > config.length - 1) return; // out of range

  const list = [...config];
  const selectedRef =
    selectedRule.value != null ? list[selectedRule.value] : null;
  list.splice(to, 0, list.splice(from, 1)[0]);

  // Update selected item index to match new list order
  if (selectedRef != null)
    selectedRule.value = list.findIndex((r) => selectedRef === r);

  kb.config.leds[kb.ledConfig] = list;
});

const removeRule = ifNoHelp((id: number) => {
  const config = kb.config.leds[kb.ledConfig];
  if (config == undefined) return;
  kb.config.leds[kb.ledConfig].splice(id, 1);
});

const addRule = ifNoHelp(() => {
  const config = kb.config.leds[kb.ledConfig];
  if (config == undefined) return;

  const rule: LedRule = {
    condition: "Always",
    // condition: { And: [ { Not: { KeyPressed: [0, 2] } }, { Or: ["UsbOn", { Layer: 2 }], }, ], },
    keys: null,
    pattern: {
      phase: { x: 0, y: 0 },
      repeat: "Once",
      transitions: [
        {
          color: [255, 255, 255],
          duration: 0,
          interpolation: "Piecewise",
        },
      ],
    },
  };

  kb.config.leds[kb.ledConfig].push(rule);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "root",
    ref: root,
    class: "p-4 flex flex-col justify-center items-center max-w-2xl w-full"
  }, [
    _createVNode(ListSwitcher, {
      class: "tour-led-configs border border-base-300 rounded-lg shadow py-3 pb-4 px-6 mb-5",
      title: "Configurations",
      tooltip: "Configurations that can be cycled through by pressing a key with the \"Led Cycle\" action",
      entries: numberList(_unref(kb).config.leds.length),
      current: _unref(kb).ledConfig,
      noDelete: _unref(kb).config.leds.length <= 1,
      onSelect: _cache[0] || (_cache[0] = (i) => (_unref(kb).ledConfig = i)),
      onInsert: _unref(kb).addLedConfig,
      onDelete: _unref(kb).removeLedConfig
    }, null, 8, ["entries", "current", "noDelete", "onInsert", "onDelete"]),
    _createVNode(_unref(Container), {
      class: "w-full flex flex-col justify-center items-center",
      onDrop: _unref(drop),
      nonDragAreaSelector: ".menu-popup"
    }, {
      default: _withCtx(() => [
        _createVNode(_TransitionGroup, null, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(ledRules.value ?? [], (_rule, id) => {
              return (_openBlock(), _createBlock(_unref(Draggable), {
                class: "w-full",
                key: id
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", {
                    class: _normalizeClass(["bg-base-100 border border-base-content/30 rounded-xl shadow mb-4 w-full relative", _unref(help).selectedLedRule == id ? 'tour-led-rule' : ''])
                  }, [
                    (isExpanded(id))
                      ? (_openBlock(), _createBlock(LedRuleEdit, {
                          key: 0,
                          modelValue: ledRules.value[id],
                          "onUpdate:modelValue": ($event: any) => ((ledRules.value[id]) = $event)
                        }, null, 8, ["modelValue", "onUpdate:modelValue"]))
                      : (_openBlock(), _createBlock(LedRuleView, {
                          key: 1,
                          rule: ledRules.value[id],
                          onClick: () => (selectedRule.value = id)
                        }, null, 8, ["rule", "onClick"])),
                    _createElementVNode("div", _hoisted_1, [
                      _createElementVNode("button", {
                        class: "btn btn-circle btn-sm btn-outline h-9 w-9 text-neutral bg-base-100 hover:border-neutral hover:bg-neutral",
                        onClick: ($event: any) => (_unref(removeRule)(id))
                      }, [
                        _createVNode(_unref(Icon), {
                          icon: "ic:baseline-delete",
                          onMouseout: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
                        })
                      ], 8, _hoisted_2)
                    ])
                  ], 2)
                ]),
                _: 2
              }, 1024))
            }), 128)),
            _createElementVNode("button", {
              key: "__add_button__",
              class: "btn btn-circle btn-sm btn-outline h-9 w-9 text-neutral hover:border-neutral hover:bg-neutral",
              onClick: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(addRule) && _unref(addRule)(...args)))
            }, [
              _createVNode(_unref(Icon), {
                icon: "ic:baseline-add",
                class: "hover:fill-neutral-content"
              })
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["onDrop"])
  ], 512))
}
}

})